import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Package, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    total_users: 0,
    total_sellers: 0,
    total_listings: 0,
    active_listings: 0,
    pending_listings: 0
  });
  const [users, setUsers] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/listings/pending`)
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPendingListings(pendingRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/role?role=${newRole}`);
      toast.success('User role updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  const handleApproveListing = async (listingId) => {
    try {
      await axios.post(`${API_URL}/api/admin/listings/${listingId}/approve`);
      toast.success('Listing approved');
      fetchData();
    } catch (error) {
      console.error('Error approving listing:', error);
      toast.error('Failed to approve listing');
    }
  };

  const handleRejectListing = async (listingId) => {
    try {
      await axios.post(`${API_URL}/api/admin/listings/${listingId}/reject`);
      toast.success('Listing rejected');
      fetchData();
    } catch (error) {
      console.error('Error rejecting listing:', error);
      toast.error('Failed to reject listing');
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="admin-dashboard-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl text-foreground tracking-tight font-semibold mb-2">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage users, listings, and platform operations
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="admin-stats-section">
          <Card className="p-6 border-border bg-card" data-testid="stat-card-users">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold text-foreground">{stats.total_users}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-border bg-card" data-testid="stat-card-sellers">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Sellers</p>
                <p className="text-2xl font-bold text-foreground">{stats.total_sellers}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-border bg-card" data-testid="stat-card-listings">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Listings</p>
                <p className="text-2xl font-bold text-foreground">{stats.total_listings}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-border bg-card" data-testid="stat-card-pending">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold text-foreground">{stats.pending_listings}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="listings" className="space-y-6" data-testid="admin-tabs">
          <TabsList>
            <TabsTrigger value="listings" data-testid="tab-listings">
              Pending Listings ({pendingListings.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              Users ({users.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            <Card className="border-border bg-card" data-testid="pending-listings-card">
              <div className="p-6">
                <h3 className="font-display text-xl text-foreground mb-4 font-semibold">Pending Listings</h3>
                {pendingListings.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No pending listings for approval
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Watch</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Condition</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingListings.map((listing) => (
                        <TableRow key={listing.id} data-testid="pending-listing-row">
                          <TableCell>
                            <div>
                              <p className="font-medium text-foreground">{listing.brand} {listing.model}</p>
                              <p className="text-sm text-muted-foreground">{listing.title}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{listing.ref_number}</TableCell>
                          <TableCell className="font-medium">{formatPrice(listing.price, listing.currency)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-transparent border-primary/30 text-foreground">
                              {listing.condition}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveListing(listing.id)}
                                data-testid="approve-listing-button"
                              >
                                <CheckCircle className="mr-1 h-4 w-4" />
                                Approve
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleRejectListing(listing.id)}
                                data-testid="reject-listing-button"
                              >
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="border-border bg-card" data-testid="users-card">
              <div className="p-6">
                <h3 className="font-display text-xl text-foreground mb-4 font-semibold">All Users</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} data-testid="user-row">
                        <TableCell className="font-medium text-foreground">{user.name}</TableCell>
                        <TableCell className="text-muted-foreground">{user.email}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              user.role === 'ADMIN'
                                ? 'bg-purple-500/20 text-purple-500'
                                : user.role === 'SELLER'
                                ? 'bg-blue-500/20 text-blue-500'
                                : 'bg-muted text-foreground'
                            }
                            data-testid={`user-role-badge-${user.role.toLowerCase()}`}
                          >
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                            data-testid="user-role-select"
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BUYER">Buyer</SelectItem>
                              <SelectItem value="SELLER">Seller</SelectItem>
                              <SelectItem value="ADMIN">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
